import { faSearch } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import debounce from 'lodash-es/debounce';
import { FAQ_EXPLANATION, FAQ_SEARCH_PLACEHOLDER, FAQ_SEARCH_RESULT_query_count } from 'mk/autogenerated/translations/FaqIndex.1c63a9a00efab4e331f2b229073f6d67'
import SearchResults from 'mk2/apps/blogs/components/SearchResults';
import TableOfContents from 'mk2/apps/faq/components/TableOfContents';
import { faqIndexUrl } from 'mk2/apps/faq/urls';
import { getWikiArticleUrl } from 'mk2/apps/wiki/urls';
import { ClearableInput } from 'mk2/components/ClearableInput';
import { Interpolate } from 'mk2/components/Interpolate';
import { ListItem } from 'mk2/components/ListItem';
import { LoadingPlaceholder } from 'mk2/components/LoadingPlaceholder';
import { LoadingSwitch } from 'mk2/containers/LoadingSwitch/LoadingSwitch';
import { astToHtml, defaultFilterAdditionalData, FilterAdditionalData } from 'mk2/helpers/article_ast/filters';
import { cacheLast } from 'mk2/helpers/cache';
import { scrollElementTo } from 'mk2/helpers/scrolling';
import { url } from 'mk2/helpers/urls';
import { isSuccess } from 'mk2/reducers/loadingState';
import { stringify } from 'query-string';
import React from 'react';
import styles from './FaqIndex.mscss';
import { Props } from './FaqIndexPage';

interface State {
    query: string;
}

class FaqIndex extends React.Component<Props, State> {

    private articleAstToHtmlCache = cacheLast(true);
    private searchRedirectDebounced: () => void;

    constructor(props) {
        super(props);

        this.state = {
            query: props.query,
        };

        this.searchRedirectDebounced = debounce(this.searchRedirect, 500);
    }

    public componentDidMount() {
        const { location, loadingState } = this.props;
        if (location.hash && isSuccess(loadingState)) {
            scrollElementTo(location.hash, 'window-top', 50);
        }
    }

    public componentDidUpdate(prevProps: Props) {
        const { location, loadingState } = this.props;

        if (
            location.hash &&
            (
                location.hash !== prevProps.location.hash ||
                loadingState !== prevProps.loadingState
            ) &&
            isSuccess(loadingState)
        ) {
            scrollElementTo(location.hash, 'window-top', 50);
        }
    }

    public render() {
        const { article, articleSlug, articles, loadingState, query } = this.props;

        return (
            <div className={styles.FaqIndex}>

                {articleSlug === 'faq' && (
                    <>
                        <div className={styles.FaqIndex__search__container}>
                            <ClearableInput
                                icon={<label htmlFor="search"><FontAwesomeIcon icon={faSearch}/></label>}
                                hasValue={Boolean(this.state.query)}
                                onClear={this.onCancelFilters}
                            >
                                <input
                                    id="search"
                                    type="text"
                                    value={this.state.query || ''}
                                    onChange={this.onSearchChange}
                                    placeholder={FAQ_SEARCH_PLACEHOLDER}
                                />
                            </ClearableInput>
                        </div>
                        {!query && (
                            <div className={styles.FaqIndex__explanation}>
                                {FAQ_EXPLANATION}
                            </div>
                        )}
                    </>
                )}

                <LoadingSwitch
                    loadingState={loadingState}
                    onLoad={this.load}
                    onRenderInit={this.renderInit}
                    onRenderSuccess={this.renderLoadSuccess}
                    hasData={!!article && articles && articles.length > 0}
                />

            </div>
        );
    }

    private renderInit = () => {
        const { isMobile } = this.props;

        const textWidths = isMobile
            ? ['100%', '100%', '100%', '60%']  // tak aby pocas loadingu bola viditelna reklama ;-)
            : ['100%', '100%', '100%', '100%', '100%', '100%', '100%', '60%'];

        return (
            <div className={styles.FaqIndex}>

                <LoadingPlaceholder className={styles.FaqIndex__titlePlaceholder} width="40%" height={35} />

                {textWidths.map((w, idx) => (
                    <LoadingPlaceholder
                        key={idx}
                        className={styles.FaqIndex__textPlaceholder}
                        width={w}
                        height={17}
                    />
                ))}

                <LoadingPlaceholder
                    className={styles.FaqIndex__imgPlaceholder}
                    width="100%"
                    height={isMobile ? 150 : 300}
                />
            </div>
        );
    };

    private renderLoadSuccess = () => {
        const { articleSlug, query } = this.props;
        return articleSlug === 'faq' && query
            ? this.renderSearchResults()
            : this.renderArticle();

    };

    private renderSearchResults = () => {
        const { articles, loadingState, query } = this.props;

        return (
            <>
                <SearchResults
                    loadingState={loadingState}
                    hasData={Boolean(articles && articles.length > 0)}
                    onCancelFilters={this.onCancelFilters}
                >
                    <Interpolate
                        i18nKey={FAQ_SEARCH_RESULT_query_count}
                        query={<strong>{query}</strong>}
                        count={articles.length}
                    />
                </SearchResults>

                <div>
                    {articles.map((articleLink) => {
                        return (
                            <ListItem
                                key={articleLink.id}
                                to={getWikiArticleUrl('faq', articleLink.slug)}
                                hasMore
                            >
                                {articleLink.title}
                            </ListItem>
                        );
                    })}
                </div>
            </>
        );
    };

    private renderArticle = () => {
        const { article, articleSlug, isMobile, requestUserPermissions } = this.props;

        if (!article.bodyAST) {
            return null;
        }

        const additionalData: FilterAdditionalData = {
            ...defaultFilterAdditionalData,
            countTopicContributors: article.countTopicContributors,
            renderContributorsCount: true,
            linkedObjects: article.bodyASTAdditionalData ? article.bodyASTAdditionalData.linkedObjects : {},
            photos: article.photos,
            customStyles: {
                header2: styles.FaqIndex__text__header2,
                header3: styles.FaqIndex__text__header3,
                header4: styles.FaqIndex__text__header4,
                orderedlist: styles.FaqIndex__text__orderedlist,
                unorderedlist: styles.FaqIndex__text__unorderedlist,
                paragraph: styles.FaqIndex__text__paragraph,
            },
            hasResponsiveImages: isMobile || requestUserPermissions.indexOf('wiki.can_edit_wiki') >= 0,
            anchorHeadings: true,
            chevronLists: true,
        };
        const bodyChunks: React.ReactChild[] = this.articleAstToHtmlCache(() => astToHtml(article.bodyAST, additionalData), article.bodyAST);

        return (
            <>
                {articleSlug === 'faq' && (
                    <TableOfContents
                        articleSlug={articleSlug}
                        bodyAST={article.bodyAST}
                    />
                )}

                <div className={styles.FaqIndex__text}>
                    {bodyChunks}
                </div>
            </>
        );
    };

    private load = () => {
        const { articleSlug, query } = this.props;
        this.props.onLoad(articleSlug, query);
    };

    private commonRedirect = (query) => {
        const { match: { params } } = this.props;

        const search = {
            ...((query !== null) ? { q: query } : {}),
        };

        this.props.history.replace({
            pathname: url(faqIndexUrl, params),
            search: search ? stringify(search) : undefined,
        });
    };

    private onSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ query: event.currentTarget.value }, this.searchRedirectDebounced);
    };

    private searchRedirect = () => {
        this.commonRedirect(this.state.query);
    };

    private onCancelFilters = () => {
        this.setState({ query: null });
        this.commonRedirect(null);
    };

}

export default FaqIndex;
